import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * MobileSheet — shared bottom-sheet primitive used by every mobile
 * slide-up panel (Hold / Scenarios / Settings / Kebab / More menu).
 *
 * Renders nothing when !isOpen. When isOpen, renders a fixed full-viewport
 * backdrop + a slide-up panel anchored to the bottom of the screen with a
 * drag-handle bar, a header (title + close X), and a scrollable body.
 *
 * Props:
 *   - isOpen, onClose (required)
 *   - title         — header text
 *   - children      — body content
 *   - height        — optional CSS height for the panel (defaults to 'auto', max 85vh)
 *   - footer        — optional sticky footer node (rendered below scrollable body)
 *   - hideHeader    — set to true to omit the header entirely
 */
export default function MobileSheet({
  isOpen,
  onClose,
  title,
  children,
  height,
  footer,
  hideHeader = false,
}) {
  // Lock body scroll while sheet is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 1200,
          animation: 'mobile-sheet-fade-in 0.18s ease-out',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1201,
          background: '#0F1530',
          borderTop: '0.5px solid rgba(255,255,255,0.08)',
          borderRadius: '16px 16px 0 0',
          maxHeight: '85vh',
          height: height || 'auto',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
          animation: 'mobile-sheet-slide-up 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            width: 40,
            height: 4,
            background: '#3a3a3a',
            borderRadius: 2,
            margin: '8px auto 4px',
            flexShrink: 0,
          }}
        />

        {/* Header */}
        {!hideHeader && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px 10px',
              borderBottom: '0.5px solid rgba(255,255,255,0.08)',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF' }}>{title}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
              }}
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Scrollable body */}
        <div
          style={{
            overflowY: 'auto',
            padding: '12px 14px 16px',
            flex: 1,
            minHeight: 0,
            color: '#FFFFFF',
          }}
        >
          {children}
        </div>

        {/* Optional sticky footer */}
        {footer && (
          <div
            style={{
              borderTop: '0.5px solid rgba(255,255,255,0.08)',
              padding: '10px 14px',
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        )}
      </div>

      {/* Local keyframes injected once via <style>; cheap inline approach */}
      <style>{`
        @keyframes mobile-sheet-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes mobile-sheet-slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
